import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import { Ansible } from 'interfaces/ansible'
import { PnsMacMini, PnsMacMiniStatus } from 'interfaces/pns-mac-mini'
import useAnsibleCapacityUpdate from './use-ansible-capacity-update'
import toast from 'react-hot-toast'

interface MacMiniAssignmentData {
  macMini: PnsMacMini
  newName: string
  ansible: Ansible
}

interface UseMacMiniAssignmentReturn {
  assignMacMinisToAnsible: (
    macMinis: PnsMacMini[],
    ansible: Ansible,
    terminalId: string
  ) => Promise<void>
  isAssigning: boolean
  assignmentProgress: {
    current: number
    total: number
    currentAction: string
    previewNames?: string[]
  } | null
}

// Helper function to extract ansible number from ansible name (e.g., "A6" -> "6", "A17" -> "17")
export const extractAnsibleNumber = (ansibleName: string): string => {
  const match = ansibleName.match(/A(\d+)/)
  return match ? match[1] : '0' // No zero padding
}

// Helper function to extract terminal prefix from terminal name (e.g., "T000" -> "", "T001" -> "T001")
export const extractTerminalPrefix = (terminalName: string): string => {
  const terminal = terminalName || 'T000'
  return terminal === 'T000' ? '' : terminal // Omit T000 prefix
}

// Helper function to find the next available Mac Mini number for an Ansible
export const findNextMacMiniNumber = (
  existingMacMinis: PnsMacMini[],
  terminalPrefix: string,
  ansibleNumber: string
): number => {
  const terminalPart = terminalPrefix ? `${terminalPrefix}` : ''
  const pattern = new RegExp(`^${terminalPart}A${ansibleNumber}M(\\d+)$`)
  const existingNumbers = existingMacMinis
    .map((macMini) => {
      const match = macMini.name.match(pattern)
      return match ? parseInt(match[1], 10) : 0
    })
    .filter((num) => num > 0)

  return existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1
}

// Helper function to generate new Mac Mini name
export const generateMacMiniName = (
  terminalName: string,
  ansibleName: string,
  macMiniNumber: number
): string => {
  const terminalPrefix = extractTerminalPrefix(terminalName)
  const ansibleNumber = extractAnsibleNumber(ansibleName)
  const terminalPart = terminalPrefix ? `${terminalPrefix}` : ''
  return `${terminalPart}A${ansibleNumber}M${macMiniNumber}`
}

// Function to update a single Mac Mini
const updateMacMini = async (
  macMiniId: string,
  updates: { name: string; status: PnsMacMiniStatus; ansibleId: string }
) => {
  let response = await fetch(
    `${BASE_API_URL}/api/v4/phone_number_service/mac_minis/${macMiniId}`,
    {
      method: API_METHODS.PUT,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify(updates),
    }
  )
  response = handleErrors(response)
  return response.json()
}

// Function to get existing Mac Minis for an Ansible
const getExistingMacMinis = async (
  ansibleId: string
): Promise<PnsMacMini[]> => {
  let response = await fetch(
    `${BASE_API_URL}/api/v4/phone_number_service/mac_minis?ansibleId=${ansibleId}&include=ansible`,
    {
      method: API_METHODS.GET,
      headers: apiHeaders(store.getState()),
    }
  )
  response = handleErrors(response)
  const data = await response.json()
  return data.data || []
}

const useMacMiniAssignmentWithNaming = (): UseMacMiniAssignmentReturn => {
  const [isAssigning, setIsAssigning] = useState(false)
  const [assignmentProgress, setAssignmentProgress] = useState<{
    current: number
    total: number
    currentAction: string
    previewNames?: string[]
  } | null>(null)

  const queryClient = useQueryClient()
  const capacityUpdateMutation = useAnsibleCapacityUpdate()

  const assignMacMinisToAnsible = useCallback(
    async (macMinis: PnsMacMini[], ansible: Ansible, terminalId: string) => {
      if (macMinis.length === 0) {
        toast.error('No Mac Minis selected for assignment')
        return
      }

      setIsAssigning(true)

      try {
        // Step 1: Get existing Mac Minis for this Ansible to determine naming
        setAssignmentProgress({
          current: 0,
          total: macMinis.length + 2, // +2 for naming fetch and capacity update
          currentAction: 'Fetching existing Mac Minis for naming...',
        })

        const existingMacMinis = await getExistingMacMinis(ansible.id)

        // Step 2: Generate names for all Mac Minis
        const terminalName = ansible.terminal?.name || 'T000'
        const nextNumber = findNextMacMiniNumber(
          existingMacMinis,
          extractTerminalPrefix(terminalName),
          extractAnsibleNumber(ansible.name)
        )

        const assignmentData: MacMiniAssignmentData[] = macMinis.map(
          (macMini, index) => ({
            macMini,
            newName: generateMacMiniName(
              terminalName,
              ansible.name,
              nextNumber + index
            ),
            ansible,
          })
        )

        // Show preview of names
        setAssignmentProgress((prev) =>
          prev
            ? {
                ...prev,
                previewNames: assignmentData.map(
                  (item) => `${item.macMini.name} → ${item.newName}`
                ),
              }
            : null
        )

        // Step 3: Update each Mac Mini with new name, status, and assignment
        const updatePromises = assignmentData.map(
          (item, index) =>
            new Promise<void>((resolve, reject) => {
              const delay = index * 100 // Stagger requests by 100ms
              setTimeout(async () => {
                try {
                  const { macMini, newName } = item

                  setAssignmentProgress({
                    current: index + 1,
                    total: macMinis.length + 2, // +2 for naming fetch and capacity update
                    currentAction: `Updating ${macMini.name} → ${newName}...`,
                    previewNames: assignmentData.map(
                      (data) => `${data.macMini.name} → ${data.newName}`
                    ),
                  })

                  await updateMacMini(macMini.id, {
                    name: newName,
                    status: PnsMacMiniStatus.PROVISIONING, // RECEIVED → PROVISIONING when assigned
                    ansibleId: ansible.id,
                  })

                  resolve()
                } catch (error) {
                  reject(error)
                }
              }, delay)
            })
        )

        await Promise.all(updatePromises)

        // Step 4: Update Ansible capacity counters
        setAssignmentProgress({
          current: macMinis.length + 1,
          total: macMinis.length + 2,
          currentAction: 'Updating ansible capacity...',
        })

        await capacityUpdateMutation.mutateAsync({
          ansibleId: ansible.id,
          currentMacMinis: ansible.currentMacMinis + macMinis.length,
        })

        // Step 5: Invalidate related queries to refresh UI
        setAssignmentProgress({
          current: macMinis.length + 2,
          total: macMinis.length + 2,
          currentAction: 'Refreshing data...',
        })

        // Invalidate all related queries
        queryClient.invalidateQueries(['received-mac-minis'])
        queryClient.invalidateQueries(['mac-minis-by-ansible'])
        queryClient.invalidateQueries(['ansible-detail', ansible.id])
        queryClient.invalidateQueries(['terminal-detail', terminalId])

        toast.success(
          `Successfully assigned ${macMinis.length} Mac Mini${
            macMinis.length === 1 ? '' : 's'
          } to ${ansible.name}`
        )
      } catch (error) {
        console.error('Mac Mini assignment failed:', error)
        toast.error(
          `Assignment failed: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        )
        throw error
      } finally {
        setIsAssigning(false)
        setAssignmentProgress(null)
      }
    },
    [queryClient, capacityUpdateMutation]
  )

  return {
    assignMacMinisToAnsible,
    isAssigning,
    assignmentProgress,
  }
}

export default useMacMiniAssignmentWithNaming
